import { JSX, Show } from 'solid-js';
import clsx from 'clsx';
import { LoadingSpinner } from '../../../loading-spinner/loading-spinner';

export type StatusNoteTone = 'neutral' | 'highlight' | 'danger';

type StatusNoteProps = {
    /** Defaults to `neutral`. */
    tone?: StatusNoteTone;
    /** Renders a spinner in the icon slot for in-flight work. */
    loading?: boolean;
    /** Leading icon. Ignored while `loading`. Should draw with `currentColor`. */
    icon?: JSX.Element;
    class?: string;
    children: JSX.Element;
};

/* DS Alert, small (Storybook `components-alert`). One surface for every transient
   message on the builder — generating / saving / deleting / busy / error — so the
   seven near-identical banners collapse to a tone. */
const TONE_CLASSES: Record<StatusNoteTone, string> = {
    neutral: 'bg-cvi-bg-neutral-subtle text-cvi-fg-default',
    highlight: 'bg-cvi-bg-status-highlight text-cvi-fg-default',
    danger: 'bg-cvi-bg-status-danger text-cvi-fg-danger',
};

export const StatusNote = (props: StatusNoteProps) => {
    const tone = () => props.tone ?? 'neutral';

    return (
        <div
            role={tone() === 'danger' ? 'alert' : 'status'}
            class={clsx(
                'flex items-center gap-cvi-5 rounded-cvi-sm p-cvi-5 body-sm font-cvi-regular print:hidden',
                TONE_CLASSES[tone()],
                props.class,
            )}
        >
            <Show when={props.loading}>
                <span class="inline-flex shrink-0" aria-hidden="true">
                    <LoadingSpinner
                        size="var(--spacing-cvi-7)"
                        thickness="var(--spacing-cvi-1)"
                        color="var(--color-cvi-fg-brand)"
                    />
                </span>
            </Show>
            <Show when={!props.loading && props.icon}>
                <span class="inline-flex shrink-0">{props.icon}</span>
            </Show>
            <div class="min-w-0 flex-1">{props.children}</div>
        </div>
    );
};
